// Floating "+" button that opens the unified ContributeSheet. Sits on the
// map at all times when a location/route is visible, so any moment of
// "this block is gorgeous" can become a data point.

import { useEffect, useRef, useState } from "react";
import { MapPinPlus } from "lucide-react";
import { toast } from "sonner";
import { ContributeSheet } from "@/components/ContributeSheet";
import type { LngLat } from "@/utils/polyline";
import type { TravelMode } from "@/api/routing";

interface Props {
  defaultLocation?: LngLat;
  mode: TravelMode;
  onAddPhoto?: () => void;
  onAddTip?: (coord: LngLat, label?: string) => void;
  /** Latest map-tap coordinate from the parent. Used to satisfy a pending
   *  "tap map to pick a spot" request initiated from inside the sheet. */
  mapTapPoint?: LngLat;
  /** Called when the user starts a tap-to-pick interaction. Parent can use
   *  it to show a hint or change the cursor. Optional. */
  onPickStart?: () => void;
}

export const ContributeFab = ({ defaultLocation, mode, onAddPhoto, onAddTip, mapTapPoint, onPickStart }: Props) => {
  const [open, setOpen] = useState(false);
  // When set, sheet is closed and we're waiting for the next map tap to
  // re-open it with that coordinate as the picked spot.
  const [awaitingTap, setAwaitingTap] = useState(false);
  const [pickedLocation, setPickedLocation] = useState<LngLat | undefined>();
  // Snapshot at the moment we start awaiting, so we only react to *new* taps.
  const baselineTapRef = useRef<LngLat | undefined>();

  useEffect(() => {
    if (!awaitingTap || !mapTapPoint) return;
    if (baselineTapRef.current
        && baselineTapRef.current[0] === mapTapPoint[0]
        && baselineTapRef.current[1] === mapTapPoint[1]) return;
    setPickedLocation(mapTapPoint);
    setAwaitingTap(false);
    setOpen(true);
  }, [awaitingTap, mapTapPoint]);

  return (
    <>
      <button
        type="button"
        onClick={() => { setPickedLocation(undefined); setOpen(true); }}
        aria-label="Add a tip about this place"
        className="fixed bottom-safe-60 right-3 md:right-5 z-30 flex flex-col items-center gap-1 group"
      >
        <span className="h-12 w-12 rounded-full bg-pleasant text-primary-foreground shadow-glow flex items-center justify-center group-hover:scale-105 transition-smooth">
          <MapPinPlus className="w-5 h-5" />
        </span>
      </button>
      <ContributeSheet
        open={open}
        onClose={() => setOpen(false)}
        defaultLocation={pickedLocation ?? defaultLocation}
        mode={mode}
        onAddPhoto={onAddPhoto}
        onAddTip={onAddTip}
        onPickOnMap={() => {
          baselineTapRef.current = mapTapPoint;
          setAwaitingTap(true);
          setOpen(false);
          onPickStart?.();
          toast("Tap anywhere on the map to pick that spot", { duration: 4000 });
        }}
      />
    </>
  );
};
